import { FilterSelectionMark } from "@/components/common/filter-selection-mark/filter-selection-mark";
import { personInitials, personName } from "@/lib/issues";
import { cn } from "@/lib/utils";
import type { ProjectUser } from "@/types/api";

interface IssuePersonFilterProps {
  title: string;
  people: ProjectUser[];
  currentUserId: string | undefined;
  /** Selected user id, or null for "anyone". */
  selected: string | null;
  onSelect: (id: string | null) => void;
  /** Extra classes for the section, so each surface sets its own spacing. */
  className?: string;
  /**
   * Render the visible title. The desktop sidebar turns it off and supplies its
   * own collapsible header; the title then labels the section for assistive tech.
   */
  showTitle?: boolean;
}

/**
 * One "who" section of the issue filters — Assigned to, or Raised by.
 *
 * Shared by [[issue-filter-drawer.spec]] and [[issue-filter-sidebar.spec]] so
 * both breakpoints offer the same people in the same order. Single-select: an
 * issue has one assignee and one raiser, so narrowing to two would match
 * nothing. See [[issue-person-filter.spec]].
 */
export function IssuePersonFilter({
  title,
  people,
  currentUserId,
  selected,
  onSelect,
  className,
  showTitle = true,
}: IssuePersonFilterProps) {
  return (
    <section
      className={cn("flex flex-col gap-2", className)}
      aria-label={showTitle ? undefined : title}
    >
      {showTitle && (
        <span className={"text-sm font-medium"}>{title}</span>
      )}
      <div className="flex flex-col">
        <PersonRow
          label="Anyone"
          selected={selected === null}
          onSelect={() => onSelect(null)}
        />
        {people.map((person) => (
          <PersonRow
            key={person.id}
            label={
              person.id === currentUserId
                ? `${personName(person)} (me)`
                : personName(person)
            }
            initials={personInitials(person)}
            selected={selected === person.id}
            onSelect={() => onSelect(person.id)}
          />
        ))}
      </div>
    </section>
  );
}

interface PersonRowProps {
  label: string;
  initials?: string;
  selected: boolean;
  onSelect: () => void;
}

function PersonRow({ label, initials, selected, onSelect }: PersonRowProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      aria-pressed={selected}
      className={cn(
        "flex items-center gap-3 rounded-md px-2 py-2 text-left transition-colors",
        // Dark mode: the chosen row lifts a step and the rest go quiet, so the
        // chosen one reads as the bright row. See [[issue-person-filter.spec]].
        selected
          ? "dark:bg-selected"
          : "dark:text-muted-foreground dark:hover:bg-card-hover dark:hover:text-foreground",
      )}
    >
      {/* Round: the list takes one person, so a square box would promise more. */}
      <FilterSelectionMark selected={selected} shape="round" />
      {initials ? (
        <span
          className={cn(
            "flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-tertiary text-[10px] font-semibold text-tertiary-foreground",
            !selected && "dark:opacity-75",
          )}
        >
          {initials}
        </span>
      ) : (
        // A blank spacer rather than no element, so "Anyone" lines up with the
        // names below it instead of sitting proud of the list.
        <span className="h-7 w-7 shrink-0" aria-hidden="true" />
      )}
      <span className={cn("flex-1 truncate text-sm", selected && "font-medium")}>
        {label}
      </span>
    </button>
  );
}
